import { useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, saveAuth } from "../lib/apiClient";
import { toast } from "sonner";

export default function AuthCallback() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 1) Implicit flow: token is in URL hash (#access_token=...)
    const hash = location.hash?.startsWith("#") ? location.hash.slice(1) : "";
    const hashParams = new URLSearchParams(hash);
    const accessToken = hashParams.get("access_token");
    const state = params.get("state") || hashParams.get("state") || "";

    // Verify flow uses the same redirect URI but a `verify:<gid>:<nonce>` state.
    // Bounce to the dedicated verify handler with tokens intact.
    if (state.startsWith("verify:")) {
      const query = new URLSearchParams(window.location.search);
      const target = `/verify/callback?${query.toString()}${location.hash || ""}`;
      navigate(target, { replace: true });
      return;
    }

    // 2) Code flow: ?code=...
    const code = params.get("code");
    const error = params.get("error") || hashParams.get("error");

    if (error) {
      toast.error(`Discord: ${error}`);
      navigate(`/?oauth_error=${encodeURIComponent(error)}`);
      return;
    }

    if (accessToken) {
      const attemptLogin = (attempt = 0) => {
        api.post("/auth/discord/token", { access_token: accessToken })
          .then(r => {
            saveAuth(r.data.token, r.data.user);
            toast.success(`Welcome, ${r.data.user.global_name || r.data.user.username}`);
            navigate("/servers");
          })
          .catch(err => {
            const status = err?.response?.status;
            if (status === 429 && attempt < 3) {
              toast.info(`Discord rate limited — retrying in ${(attempt + 1) * 3}s...`);
              setTimeout(() => attemptLogin(attempt + 1), (attempt + 1) * 3000);
              return;
            }
            toast.error(err?.response?.data?.detail || "Discord login failed");
            navigate("/");
          });
      };
      attemptLogin();
      return;
    }

    if (code) {
      api.post("/auth/discord/callback", { code })
        .then(r => {
          saveAuth(r.data.token, r.data.user);
          toast.success(`Welcome, ${r.data.user.global_name || r.data.user.username}`);
          navigate("/servers");
        })
        .catch(err => {
          toast.error(err?.response?.data?.detail || "OAuth callback failed");
          navigate("/");
        });
      return;
    }

    toast.error("No auth data returned");
    navigate("/");
  }, [params, navigate, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#06070A] mm-grid-bg" data-testid="auth-callback">
      <div className="font-mono text-[#10B981] tracking-widest text-sm">
        <span className="pulse-dot inline-block w-2 h-2 bg-[#10B981] mr-3" />
        ESTABLISHING SECURE HANDSHAKE...
      </div>
    </div>
  );
}
